<script lang="ts">
    import VVMArrowLeftIcon from '$lib/assets/icons/VVMArrowLeftIcon.svelte';
    import VVMArrowRightIcon from '$lib/assets/icons/VVMArrowRightIcon.svelte';
    import type { ApiSchemas } from '$lib/api/shopwareApi';
    import { useLocaleContext, useShopContext } from '$lib/context';
    import { t } from 'svelte-i18n';
    import { createQuery } from '@tanstack/svelte-query';
    import { ProductApi } from '$lib/api/productApi';
    import { CartApi } from '$lib/api/cartApi';
    import GenericProductListing from '$lib/components/product/GenericProductListing.svelte';
    import Swiper from 'swiper';
    import { Pagination } from 'swiper/modules';
    import 'swiper/css';
    import 'swiper/css/pagination';
    import 'swiper/css/navigation';
    import { onMount } from 'svelte';

    const lang = useLocaleContext();
    const shopContext = useShopContext();

    export let categoryDisplayStyle: 'wines' | 'tickets' | 'packages' = 'wines';
    export let category: string | undefined = undefined;
    export let properties: string[] | undefined = undefined;
    export let title: string;
    export let exclude: string | undefined = undefined;
    let classes = '';
    export { classes as class };

    $: productsQuery = createQuery<{ elements: ApiSchemas['Product'][] }>({
        queryKey: ['products', category, properties, exclude],
        queryFn: ({ queryKey }) =>
            ProductApi.getFilteredParentProducts(
                {
                    category: queryKey[1] as string,
                    properties: queryKey[2] as string[],
                    exclude: queryKey[3] as string
                },
                shopContext
            ),
        staleTime: 120 * 1000,
        gcTime: 180 * 1000
    });
    let products: ApiSchemas['Product'][];
    $: products = $productsQuery.data?.elements ?? [];

    $: cartQuery = createQuery({
        queryKey: ['cart'],
        queryFn: () => CartApi.getCart(shopContext),
        staleTime: 30 * 1000,
        gcTime: 120 * 1000
    });
    let cart: ApiSchemas['Cart'];
    $: cart = $cartQuery.data;

    // Swiper
    let swiper: Swiper;
    let swiperEl: HTMLDivElement;
    let swiperInitialized = false;

    $: if (!swiperInitialized && swiperEl) {
        swiper = new Swiper(swiperEl, {
            observer: true,
            observeParents: true,
            modules: [Pagination],
            slidesPerView: 1,
            spaceBetween: 32,
            breakpoints: {
                640: {
                    pagination: false,
                    slidesPerView: 2
                },
                1080: {
                    pagination: false,
                    slidesPerView: 3
                },
                1440: {
                    pagination: false,
                    slidesPerView: 4
                }
            },
            pagination: {
                clickable: true,
                el: '.swiper-pagination'
            }
        });
        swiperInitialized = true;
    }
</script>

<div class="uppercase flex flex-col my-0 gap-6 font-jost w-full max-w-screen-2xl mx-auto mt-28 md:mt-48 {classes}">
    <div class="flex justify-between items-center px-5 w-full">
        <h2 class="text-primary tracking-[0.1em] text-18px 2xl:text-21px font-medium">{title ?? ''}</h2>
        <div class="flex gap-8 items-center">
            <a
                href="/{lang}/search"
                class="hidden sm:block text-sm border-b border-accent text-accent hover:text-primary hover:border-primary transition-colors p-0.5 pb-1.5 font-medium tracking-[0.06rem]"
                >{$t('shop.scrollable-list.discover-all')}</a
            >
            <div class="hidden sm:flex gap-4 items-center">
                <button
                    class="size-11 border border-primary flex justify-center items-center group hover:bg-primary transition-colors"
                    on:click={() => swiper.slidePrev()}
                >
                    <VVMArrowLeftIcon />
                </button>
                <button
                    class="size-11 border border-primary flex justify-center items-center group hover:bg-primary transition-colors"
                    on:click={() => swiper.slideNext()}
                >
                    <VVMArrowRightIcon />
                </button>
            </div>
        </div>
    </div>
    <div
        class="overflow-scroll no-scrollbar {categoryDisplayStyle === 'tickets'
            ? 'min-h-[195px] sm:min-h-[362px]'
            : 'min-h-[277px] sm:min-h-[654px]'}"
    >
        <div class="swiper !px-5" bind:this={swiperEl}>
            <div class="swiper-wrapper">
                {#each products ?? [] as product (product?.id)}
                    <GenericProductListing {product} {cart} />
                {/each}
            </div>
            <div class="swiper-pagination"></div>
        </div>
    </div>
    <div class="sm:hidden flex flex-col items-center gap-7">
        <a
            href="/{lang}/search"
            class="block w-fit sm:hidden text-[0.813rem] font-medium tracking-[0.063rem] border-b border-accent text-accent pb-1.5"
            >{$t('shop.scrollable-list.discover-all')}
        </a>
    </div>
</div>

<style lang="postcss">
    :global(.swiper-pagination-bullet) {
        width: 10px;
        height: 10px;
        border: 1px solid #b42133;
        opacity: 1;
        background: transparent;
    }

    :global(.swiper-pagination-bullet-active) {
        width: 10px;
        height: 10px;
        border: 1px solid #b42133;
        background: #b42133;
    }
</style>
